import { InstanceServiceContainer } from '../InstanceServiceContainer.js';
import { ITransactionItem } from './ITransactionItem.js';
import { ChangeGroup } from "./ChangeGroup.js";
import { IUndoService } from './IUndoService.js';
import { IDesignerCanvas } from '../../widgets/designerView/IDesignerCanvas.js';
import { TypedEvent } from '@node-projects/base-custom-webcomponent';
import { IUndoChangeEvent, UndoChangeKind, UndoChangeSource } from './IUndoChangeEvent.js';
import { IContentChanged } from '../InstanceServiceContainer.js';

/*
 * Manages a stack of available undo/redo actions
 */
export class UndoService implements IUndoService {
  private _undoStack: ITransactionItem[] = [];
  private _redoStack: ITransactionItem[] = [];
  private _transactionStack: ChangeGroup[] = [];
  private _instanceServiceContainer: InstanceServiceContainer;
  private _storeRedoBranches: boolean;

  constructor(designerCanvas: IDesignerCanvas | InstanceServiceContainer, storeRedoBranches: boolean = false) {
    this._instanceServiceContainer = designerCanvas instanceof InstanceServiceContainer ? designerCanvas : designerCanvas.instanceServiceContainer;
    this._storeRedoBranches = storeRedoBranches;
  }

  openGroup(title: string, source: UndoChangeSource = 'local'): ChangeGroup {
    this._instanceServiceContainer.editingDocument?.assertAlive();
    let t = new ChangeGroup(title, (t) => this.commitTransactionItem(t), (t) => this.abortTransactionItem(t), source);
    this._transactionStack.push(t);
    return t;
  }

  private commitTransactionItem(transactionItem: ChangeGroup) {
    let itm = this._transactionStack.pop();
    if (itm !== transactionItem) {
      this.clear();
      throw "UndoService - Commited Transaction was not the last";
    }
    if (itm.undoStack.length) {
      if (this._transactionStack.length > 0) {
        this._transactionStack[this._transactionStack.length - 1].addCommitedSubchangeGroup(itm);
      } else {
        if (this._storeRedoBranches && this._redoStack.length) {
          if (itm.redoBranches == null)
            itm.redoBranches = [];
          itm.redoBranches.push(this._redoStack);
        }
        this._redoStack = [];
        this._undoStack.push(itm);
      }
    }
    if (this._transactionStack.length == 0) {
      this._instanceServiceContainer.refreshExtensions(transactionItem.affectedItems);
      this.emitTransaction(transactionItem, 'execute', (<ChangeGroup>transactionItem).source ?? 'local');
      if (transactionItem.contentChanges)
        this._instanceServiceContainer.onContentChanged.emit(transactionItem.contentChanges);
    }
  }

  private abortTransactionItem(transactionItem: ChangeGroup) {
    if (this._transactionStack.length > 0) {
      let itm = this._transactionStack.pop();
      if (itm !== transactionItem) {
        this.clear();
        throw "UndoService - Aborted Transaction was not the last";
      }
      itm.undo();
    }
  }

  execute(item: ITransactionItem, source: UndoChangeSource = 'local') {
    this._instanceServiceContainer.editingDocument?.assertAlive();
    let changeItems: IContentChanged[] | null = null;
    if (this._transactionStack.length == 0) {
      changeItems = item.do();
      if (this._storeRedoBranches && this._redoStack.length) {
        if (item.redoBranches == null)
          item.redoBranches = [];
        item.redoBranches.push(this._redoStack);
      }
      this._redoStack = [];
      this._undoStack.push(item);
    } else {
      changeItems = this._transactionStack[this._transactionStack.length - 1].execute(item);
    }
    if (this._transactionStack.length == 0) {
      this._instanceServiceContainer.refreshExtensions(item.affectedItems);
      this.emitTransaction(item, 'execute', source);
      if (changeItems)
        this._instanceServiceContainer.onContentChanged.emit(changeItems);
    }
  }

  clear() {
    this._undoStack = [];
    this._redoStack = [];
    this._transactionStack = [];
  }

  clearTransactionstackIfNotEmpty() {
    if (this._transactionStack.length) {
      console.error("transactionStack was not empty, but should be", this._transactionStack);
      this._transactionStack = [];
    }
  }

  undo(source: UndoChangeSource = 'local') {
    this._instanceServiceContainer.editingDocument?.assertAlive();
    if (!this.canUndo())
      return;
    if (this._transactionStack.length != 0)
      throw "Cannot Undo while transaction is running";

    let item = this._undoStack.pop();
    let changeItems: IContentChanged[] | null = null;
    try {
      changeItems = item.undo();
      this._redoStack.push(item);
    } catch (err) {
      this.clear();
      throw err;
    }
    this._instanceServiceContainer.refreshExtensions(item.affectedItems);
    this.emitTransaction(item, 'undo', source);
    if (changeItems)
      this._instanceServiceContainer.onContentChanged.emit(changeItems);
  }

  redo(source: UndoChangeSource = 'local') {
    this._instanceServiceContainer.editingDocument?.assertAlive();
    if (!this.canRedo())
      return;
    if (this._transactionStack.length != 0)
      throw "Cannot Redo while transaction is running";

    let item = this._redoStack.pop();
    let changeItems: IContentChanged[] | null = null;
    try {
      changeItems = item.do();
      this._undoStack.push(item);
    } catch (err) {
      this.clear();
      throw err;
    }
    this._instanceServiceContainer.refreshExtensions(item.affectedItems);
    this.emitTransaction(item, 'redo', source);
    if (changeItems)
      this._instanceServiceContainer.onContentChanged.emit(changeItems);
  }

  redoTo(transactionItems: ITransactionItem[]) {
    this._redoStack = transactionItems.toReversed();
    for (let n = 0; n < transactionItems.length; n++)
      this.redo();
  }

  canUndo(): boolean {
    return this._undoStack.length > 0;
  }

  canRedo(): boolean {
    return this._redoStack.length > 0;
  }

  get undoCount(): number {
    return this._undoStack.length;
  }

  get redoCount(): number {
    return this._redoStack.length;
  }

  *getUndoEntryNames(count: number = 999): Generator<string, void, unknown> {
    for (let i = Math.min(this._undoStack.length, count) - 1; i >= 0; i--)
      yield this._undoStack[i].title;
  }

  *getUndoEntries(count: number = 999): Generator<ITransactionItem, void, unknown> {
    for (let i = Math.min(this._undoStack.length, count) - 1; i >= 0; i--)
      yield this._undoStack[i];
  }

  *getRedoEntryNames(count: number = 999): Generator<string, void, unknown> {
    for (let i = Math.min(this._redoStack.length, count) - 1; i >= 0; i--)
      yield this._redoStack[i].title;
  }

  *getRedoEntries(count: number = 999): Generator<ITransactionItem, void, unknown> {
    for (let i = Math.min(this._redoStack.length, count) - 1; i >= 0; i--)
      yield this._redoStack[i];
  }

  /** Event emitted when a transaction occurs, needed for remote collaboration */
  readonly onTransaction = new TypedEvent<IUndoChangeEvent>();

  private emitTransaction(item: ITransactionItem, kind: UndoChangeKind, source: UndoChangeSource) {
    this.onTransaction.emit({ item, kind, source });
  }
}
